/**
 * API client used by every page/hook.
 *
 * Auth strategy:
 *   - Primary: the server sets an httpOnly cookie on POST /api/auth/login.
 *     `credentials: "include"` makes the browser send it automatically on
 *     same-origin requests. The token is NEVER exposed to JS.
 *   - Fallback (only if cookies are blocked, e.g. some iframe sandboxes):
 *     after a successful login we check GET /api/auth/me. If still 401, the
 *     login response body's access_token is stored in a module-local variable
 *     (in-memory only, lost on tab close, never in localStorage) and sent as
 *     `Authorization: Bearer ...`.
 */

const API_BASE = "/api";

let bearerFallback: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setBearerFallback(token: string | null): void {
  bearerFallback = token;
}

export function hasBearerFallback(): boolean {
  return bearerFallback !== null;
}

export function registerUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

export class ApiError extends Error {
  status: number;
  code: string;
  details: Record<string, unknown>;
  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type RequestOpts = {
  method?: string;
  body?: unknown;
  formData?: FormData;
  signal?: AbortSignal;
  asBlob?: boolean;
  query?: Record<string, string | number | undefined | null | string[]>;
  headers?: Record<string, string>;
};

function buildUrl(path: string, query?: RequestOpts["query"]): string {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  if (!query) return url;
  const u = new URL(url, window.location.origin);
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) v.forEach((item) => u.searchParams.append(k, String(item)));
    else u.searchParams.set(k, String(v));
  }
  return u.pathname + (u.search || "");
}

export async function apiRequest<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const method = (opts.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(opts.headers ?? {}),
  };
  if (method !== "GET" && method !== "HEAD") {
    headers["X-Requested-With"] = "fetch";
  }
  if (bearerFallback) {
    headers["Authorization"] = `Bearer ${bearerFallback}`;
  }

  let body: BodyInit | undefined;
  if (opts.formData) {
    body = opts.formData;
  } else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }

  const res = await fetch(buildUrl(path, opts.query), {
    method,
    headers,
    body,
    credentials: "include",
    signal: opts.signal,
  });

  if (res.status === 401 && onUnauthorized) {
    onUnauthorized();
  }

  if (opts.asBlob) {
    if (!res.ok) {
      throw new ApiError(res.status, `http_${res.status}`, res.statusText || "Request failed");
    }
    return (await res.blob()) as unknown as T;
  }

  // Parse body – accept empty too
  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (!res.ok) {
    const env = (data ?? {}) as { error?: { code?: string; message?: string; details?: Record<string, unknown> } };
    const err = env.error;
    throw new ApiError(
      res.status,
      err?.code ?? `http_${res.status}`,
      err?.message ?? res.statusText ?? "Request failed",
      err?.details ?? {},
    );
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
